'use client'

import { motion } from 'framer-motion'
import { MessageCircle, Mail, Calendar, Clock, ArrowRight, Phone, ShieldCheck, CheckCircle2 } from 'lucide-react'
import { EASE_OUT, EASE_CUSTOM } from '@/lib/animations'
import ContactForm from '@/components/ContactForm'

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.9, ease: EASE_CUSTOM },
}

const stagger = (i: number) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-40px' },
  transition: { duration: 0.8, delay: i * 0.1, ease: EASE_CUSTOM },
})

const contactCards = [
  {
    icon: MessageCircle,
    title: 'WhatsApp',
    subtitle: 'Respuesta en minutos',
    value: '+34 672 724 676',
    label: 'Escríbenos',
    href: 'https://wa.me/34672724676?text=Hola%20Innovathera,%20quiero%20hablar%20sobre%20mi%20proyecto.',
    badge: 'Más rápido',
  },
  {
    icon: Mail,
    title: 'Email',
    subtitle: 'Para consultas detalladas',
    value: 'info@innovatheratech.com',
    label: 'Enviar correo',
    href: 'mailto:info@innovatheratech.com',
    badge: null,
  },
  {
    icon: Calendar,
    title: 'Videollamada',
    subtitle: 'Diagnóstico gratuito',
    value: 'calendly.com/innovatheratech',
    label: 'Agendar llamada',
    href: 'https://calendly.com/innovatheratech/30min',
    badge: 'Sin coste',
  },
]

const promises = [
  'Respuesta en menos de 24h laborables',
  'Diagnóstico inicial 100% gratuito',
  'Sin intermediarios — hablas con el equipo',
  'Propuesta sin compromiso · Verifactu vía Holded',
]

export default function ContactoClient() {
  return (
    <>
      {/* ── Hero ── */}
      <section className="pt-28 pb-12 relative z-10">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">

          <motion.div {...fadeUp} className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 border border-blue-100 mb-5">
              <Phone className="w-3.5 h-3.5 text-blue-600" />
              <span className="text-xs font-mono tracking-[0.25em] text-blue-600 uppercase">Estamos aquí</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tight text-slate-900 mb-4">
              Cuéntanos qué necesitas{' '}
              <span className="text-slate-400 font-light">y te lo construimos.</span>
            </h1>
            <p className="text-slate-500 text-lg font-light max-w-2xl mx-auto leading-relaxed">
              Diagnóstico previo gratuito y propuesta personalizada con ROI proyectado.
              Tres formas de empezar — tú eliges la que prefieras.
            </p>
          </motion.div>

          {/* Contact cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {contactCards.map((card, i) => {
              const Icon = card.icon
              return (
                <motion.div
                  key={card.title}
                  {...stagger(i)}
                  className="glass-card rounded-2xl p-6 flex flex-col gap-4"
                >
                  <div className="flex items-start justify-between">
                    <div className="w-11 h-11 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-blue-600" />
                    </div>
                    {card.badge && (
                      <span className="text-[10px] font-bold px-2.5 py-1 rounded-full border bg-blue-50 text-blue-600 border-blue-100 tracking-wide uppercase">
                        {card.badge}
                      </span>
                    )}
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 text-base">{card.title}</p>
                    <p className="text-slate-400 text-xs font-mono mt-0.5">{card.subtitle}</p>
                    <p className="text-slate-600 text-sm mt-2 font-medium break-all">{card.value}</p>
                  </div>
                  <a
                    href={card.href}
                    target={card.href.startsWith('http') ? '_blank' : undefined}
                    rel={card.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="mt-auto inline-flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 transition-all shadow-[0_4px_16px_rgba(37,99,235,0.3)] active:scale-95"
                  >
                    {card.label}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ── Formulario único genérico ── */}
      <ContactForm />

      {/* ── Footer info: horario + promesa ── */}
      <section className="pb-20 relative z-10">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* Horario */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.8, ease: EASE_CUSTOM }}
              className="glass-card rounded-2xl p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center">
                  <Clock className="w-4 h-4 text-blue-600" />
                </div>
                <h3 className="font-bold text-slate-900 text-sm">Horario de atención</h3>
              </div>
              <div className="space-y-2.5">
                <div className="flex items-start justify-between py-2 border-b border-slate-200/30 gap-3">
                  <div>
                    <span className="text-slate-700 text-sm font-medium">Videollamada (Calendly)</span>
                    <p className="text-slate-400 text-xs mt-0.5">Diagnóstico gratuito</p>
                  </div>
                  <span className="font-semibold text-slate-800 text-xs text-right shrink-0">Lun – Vie<br />9:00 – 18:00</span>
                </div>
                <div className="flex items-start justify-between py-2 gap-3">
                  <div>
                    <span className="text-slate-700 text-sm font-medium">Email & WhatsApp</span>
                    <p className="text-slate-400 text-xs mt-0.5">Consultas y soporte</p>
                  </div>
                  <span className="font-semibold text-slate-800 text-xs text-right shrink-0">Lun – Sáb<br />Respuesta &lt; 24h</span>
                </div>
              </div>
            </motion.div>

            {/* Promesa */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.8, delay: 0.1, ease: EASE_CUSTOM }}
              className="glass-card rounded-2xl p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center">
                  <ShieldCheck className="w-4 h-4 text-blue-600" />
                </div>
                <h3 className="font-bold text-slate-900 text-sm">Nuestra promesa</h3>
              </div>
              <ul className="space-y-2.5">
                {promises.map(item => (
                  <li key={item} className="flex items-center gap-2 text-slate-600 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-500 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  )
}
